<template>
  <aside
    class="c-sidebar"
    :class="{ 'c-sidebar--collapsed': collapsed, 'c-sidebar--mobile-open': mobileOpen }"
  >
    <div class="c-sidebar__head">
      <button
        type="button"
        class="c-sidebar__brand"
        title="Home"
        @click="$emit('home')"
      >
        <img
          v-if="!collapsed"
          class="c-sidebar__logo"
          src="../../assets/betty-logo.svg"
          alt="Meridianbet"
        >
        <img
          v-else
          class="c-sidebar__logo-mark"
          src="../../assets/betty-logo-2.svg"
          alt="Meridianbet"
        >
      </button>
    </div>

    <nav
      class="c-sidebar__nav"
      aria-label="Main navigation"
    >
      <p
        v-if="!collapsed"
        class="c-sidebar__section-label"
      >
        Menu
      </p>

      <ul class="c-sidebar__list">
        <li
          v-for="item in items"
          :key="item.key"
        >
          <template v-if="hasChildren(item)">
            <button
              type="button"
              class="c-sidebar__item"
              :class="{ 'c-sidebar__item--parent-active': isGroupActive(item) }"
              :title="collapsed ? item.text : null"
              :aria-expanded="String(isExpanded(item))"
              @click="toggleGroup(item)"
            >
              <app-icon
                class="c-sidebar__item-icon"
                :name="item.icon"
              />
              <span class="c-sidebar__item-text">{{ item.text }}</span>
              <app-icon
                class="c-sidebar__chevron"
                :class="{ 'c-sidebar__chevron--open': isExpanded(item) }"
                name="expand_more"
                :size="18"
              />
            </button>

            <transition name="c-sidebar-expand">
              <ul
                v-if="isExpanded(item) && !collapsed"
                class="c-sidebar__sublist"
              >
                <li
                  v-for="subItem in item.subItems"
                  :key="subItem.key"
                >
                  <router-link
                    class="c-sidebar__subitem"
                    :class="{ 'c-sidebar__subitem--active': isActive(subItem) }"
                    :to="{ name: subItem.pathName }"
                  >
                    {{ subItem.text }}
                  </router-link>
                </li>
              </ul>
            </transition>
          </template>

          <router-link
            v-else
            class="c-sidebar__item"
            :class="{ 'c-sidebar__item--active': isActive(item) }"
            :to="{ name: item.pathName }"
            :title="collapsed ? item.text : null"
          >
            <app-icon
              class="c-sidebar__item-icon"
              :name="item.icon"
              :filled="isActive(item)"
            />
            <span class="c-sidebar__item-text">{{ item.text }}</span>
          </router-link>
        </li>
      </ul>
    </nav>

    <div class="c-sidebar__foot">
      <button
        type="button"
        class="c-sidebar__item c-sidebar__collapse"
        :title="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        @click="$emit('toggle-collapse')"
      >
        <app-icon
          class="c-sidebar__item-icon"
          :name="collapsed ? 'left_panel_open' : 'left_panel_close'"
        />
        <span class="c-sidebar__item-text">Collapse</span>
      </button>
    </div>
  </aside>
</template>

<script>
import AppIcon from '../app-icon/AppIcon';
import { isNavItemActive } from './nav';

export default {
  name: 'AppSidebar',
  components: {
    AppIcon,
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
    mobileOpen: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    expandedKeys: [],
  }),
  watch: {
    '$route.name': {
      immediate: true,
      handler() {
        this.items
          .filter(item => this.isGroupActive(item) && !this.expandedKeys.includes(item.key))
          .forEach(item => this.expandedKeys.push(item.key));
      },
    },
  },
  methods: {
    hasChildren(item) {
      return !!(item.subItems && item.subItems.length);
    },

    isActive(item) {
      return isNavItemActive(this.$route, item);
    },

    isGroupActive(item) {
      return this.hasChildren(item) && item.subItems.some(this.isActive);
    },

    isExpanded(item) {
      return this.expandedKeys.includes(item.key);
    },

    toggleGroup(item) {
      if (this.collapsed) {
        // a collapsed rail has no room for children: expand the sidebar and open the group
        this.$emit('toggle-collapse');
        if (!this.isExpanded(item)) {
          this.expandedKeys.push(item.key);
        }
        return;
      }

      this.expandedKeys = this.isExpanded(item)
        ? this.expandedKeys.filter(key => key !== item.key)
        : [...this.expandedKeys, item.key];
    },
  },
};
</script>

<style lang="scss" scoped>
.c-sidebar {
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 264px;
  height: 100vh;
  background-color: var(--sidebar-bg);
  border-right: 1px solid var(--sidebar-border);
  font-family: var(--font-sans);
  transition: width var(--duration-base) var(--ease-out);
  z-index: $zIndexDialog;
}

.c-sidebar--collapsed {
  width: 72px;

  .c-sidebar__item-text,
  .c-sidebar__chevron {
    display: none;
  }

  .c-sidebar__item {
    justify-content: center;
    padding: 0;
  }
}

// ---------- head ----------
.c-sidebar__head {
  display: flex;
  align-items: center;
  height: 64px;
  flex-shrink: 0;
  padding: 0 20px;
  border-bottom: 1px solid var(--sidebar-border);

  .c-sidebar--collapsed & {
    justify-content: center;
    padding: 0;
  }
}

.c-sidebar__brand {
  display: flex;
  align-items: center;
  padding: 6px;
  margin-left: -6px;
  border-radius: var(--radius-md);
  background: transparent;
  cursor: pointer;

  .c-sidebar--collapsed & {
    margin-left: 0;
  }

  &:focus-visible {
    box-shadow: var(--focus-ring);
  }
}

.c-sidebar__logo {
  width: 148px;
}

.c-sidebar__logo-mark {
  width: 32px;
  height: 32px;
}

// ---------- nav ----------
.c-sidebar__nav {
  flex: 1;
  min-height: 0;
  padding: 16px 12px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--sidebar-border) transparent;
}

.c-sidebar__section-label {
  margin: 0 0 8px;
  padding: 0 12px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b707a;
}

.c-sidebar__list,
.c-sidebar__sublist {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0;
}

.c-sidebar__item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--sidebar-text) !important;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  text-decoration: none !important;
  cursor: pointer;
  transition: background-color var(--duration-fast) ease, color var(--duration-fast) ease;

  &:hover {
    background-color: var(--sidebar-hover);
    color: var(--sidebar-text-strong) !important;
  }

  &:focus-visible {
    box-shadow: var(--focus-ring);
  }
}

.c-sidebar__item--active {
  background-color: var(--sidebar-active-bg);
  color: var(--sidebar-active-text) !important;

  &:hover {
    background-color: var(--sidebar-active-bg);
  }

  .c-sidebar__item-icon {
    color: var(--sidebar-accent);
  }

  // accent bar on the left edge
  &::before {
    content: '';
    position: absolute;
    left: -12px;
    top: 8px;
    bottom: 8px;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background-color: var(--sidebar-accent);
  }
}

.c-sidebar__item--parent-active {
  color: var(--sidebar-text-strong) !important;

  .c-sidebar__item-icon {
    color: var(--sidebar-accent);
  }
}

.c-sidebar__item-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.c-sidebar__chevron {
  transition: transform var(--duration-base) var(--ease-out);
}

.c-sidebar__chevron--open {
  transform: rotate(180deg);
}

// ---------- sub items ----------
.c-sidebar__sublist {
  position: relative;
  margin: 2px 0 6px 22px;
  padding-left: 12px;
  border-left: 1px solid var(--sidebar-border);
}

.c-sidebar__subitem {
  display: flex;
  align-items: center;
  height: 34px;
  padding: 0 12px;
  border-radius: var(--radius-sm);
  color: var(--sidebar-text) !important;
  font-size: 13.5px;
  text-decoration: none !important;
  transition: background-color var(--duration-fast) ease, color var(--duration-fast) ease;

  &:hover {
    background-color: var(--sidebar-hover);
    color: var(--sidebar-text-strong) !important;
  }

  &:focus-visible {
    box-shadow: var(--focus-ring);
  }
}

.c-sidebar__subitem--active {
  color: var(--sidebar-active-text) !important;
  font-weight: 600;
  background-color: var(--sidebar-active-bg);

  &:hover {
    background-color: var(--sidebar-active-bg);
  }
}

.c-sidebar-expand-enter-active,
.c-sidebar-expand-leave-active {
  transition: opacity var(--duration-base) ease, transform var(--duration-base) var(--ease-out);
}

.c-sidebar-expand-enter,
.c-sidebar-expand-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

// ---------- foot ----------
.c-sidebar__foot {
  flex-shrink: 0;
  padding: 12px;
  border-top: 1px solid var(--sidebar-border);
}

// ---------- mobile: off-canvas ----------
// ---------- mobile: icon rail is always visible, expanding overlays the content ----------
@media (max-width: 960px) {
  .c-sidebar--mobile-open {
    width: 264px;
    margin-right: -192px; // keep the 72px rail footprint so content doesn't reflow
    box-shadow: var(--shadow-xl);
  }
}

@media (prefers-reduced-motion: reduce) {
  .c-sidebar,
  .c-sidebar__chevron {
    transition: none;
  }
}
</style>
